<template>
  <div class="invitations-list">
    <div style="margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #ddd">
      <h4 style="margin: 0 0 8px 0; font-size: 14px">{{ s__('ProjectSettings|Invite User') }}</h4>
      <div class="info-block invitation-info-block">
        <div class="info-block-header">
          <span class="info-icon"><InfoIcon /></span>
          <span class="info-title">{{ s__('ProjectSettings|Invitation guide') }}</span>
        </div>
        <div class="info-block-content">
          <p>
            {{
              s__(
                'ProjectSettings|Users must be registered in the app to be found for invitation. To register, they can message the bot in Telegram, or sign up with email.'
              )
            }}
          </p>
        </div>
      </div>
      <div
        v-if="memberLimitReached"
        ref="memberLimitNoticeEl"
        class="member-limit-notice"
        :class="{ 'member-limit-notice--emphasize': memberLimitNoticeEmphasize }"
        @animationend.self="onMemberLimitNoticeEmphasizeEnd"
      >
        <p class="member-limit-notice__text">
          {{
            s__(
              'ProjectSettings|Free projects can include up to 7 members. You can remove a member to invite someone new. Upgrading this project for a higher member limit is on the roadmap and not available yet.'
            )
          }}
        </p>
        <p class="member-limit-notice__text">
          {{
            s__(
              'ProjectSettings|Need a higher member limit or help upgrading this project? Contact us on Telegram'
            )
          }}
          <span class="copyable-inline copyable-inline--popover">
            <code class="contact-mono">{{ telegramSupportHandle }}</code>
            <span
              v-if="copyFeedbackKind === 'telegram'"
              :key="copyFeedbackTick"
              class="copy-contact-feedback-bubble"
              @animationend="onCopyFeedbackAnimationEnd"
            >
              {{ copiedLabel }}
            </span>
            <button
              type="button"
              class="copy-contact-btn"
              :aria-label="copyTelegramLabel"
              @click.stop="copyContact('telegram')"
            >
              <CopyIcon size="14" color="#666" />
            </button>
          </span>
          {{ s__('Project| or at') }}
          <span class="copyable-inline copyable-inline--popover">
            <code class="contact-mono">{{ supportEmail }}</code>
            <span
              v-if="copyFeedbackKind === 'email'"
              :key="copyFeedbackTick"
              class="copy-contact-feedback-bubble"
              @animationend="onCopyFeedbackAnimationEnd"
            >
              {{ copiedLabel }}
            </span>
            <button
              type="button"
              class="copy-contact-btn"
              :aria-label="copyEmailLabel"
              @click.stop="copyContact('email')"
            >
              <CopyIcon size="14" color="#666" />
            </button>
          </span>
          .
        </p>
      </div>
      <div class="invite-controls-row">
        <user-select
          v-model="newInvitationUserId"
          :searchable="true"
          :search-placeholder="s__('ProjectSettings|Search by name or username...')"
          :placeholder="s__('ProjectSettings|Select user to invite')"
          :show-empty-option="false"
          :users="[]"
          :disabled="memberLimitReached"
          class="invite-controls-row__select"
        />
        <button
          class="btn-primary btn-sm invite-controls-row__submit"
          :disabled="!newInvitationUserId || creatingInvitation || memberLimitReached"
          @click="createInvitation"
        >
          <span v-if="creatingInvitation">{{ s__('ProjectSettings|Inviting...') }}</span>
          <span v-else>{{ s__('ProjectSettings|Invite') }}</span>
        </button>
        <div
          v-if="memberLimitReached"
          class="member-limit-invite-intercept"
          role="presentation"
          @click="emphasizeMemberLimitNotice"
          @mouseenter="emphasizeMemberLimitNotice"
        />
      </div>
      <div v-if="invitationError" class="error" style="margin-top: 8px">{{ invitationError }}</div>
    </div>

    <div v-if="loading" class="muted" style="margin-top: 12px">{{ __('Loading…') }}</div>
    <div v-else-if="invitations.length === 0" class="muted" style="margin-top: 12px">
      {{ s__('ProjectSettings|No invitations yet.') }}
    </div>
    <ul v-else style="list-style: none; padding: 0; margin: 0 0 12px 0">
      <li
        v-for="invitation in invitations"
        :key="invitation.id"
        class="invitation-item"
        style="padding: 12px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 8px"
      >
        <div style="display: flex; justify-content: space-between; align-items: flex-start">
          <div style="flex: 1">
            <div>
              <strong>{{
                invitation.user.name || invitation.user.username || `User ${invitation.user.id}`
              }}</strong>
            </div>
            <div class="muted" style="font-size: 13px; margin-top: 2px">
              @{{ invitation.user.username || 'no-username' }}
            </div>
            <div
              style="margin-top: 8px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap"
            >
              <span
                class="status-badge"
                :class="`status-${invitation.status}`"
                style="padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 500"
              >
                {{ formatInvitationStatus(invitation.status) }}
              </span>
              <span v-if="invitation.deliveryAttempts > 0" class="muted" style="font-size: 12px">
                {{ s__('ProjectSettings|Attempts') }}: {{ invitation.deliveryAttempts }}/5
              </span>
              <span v-if="invitation.inviter" class="muted" style="font-size: 12px">
                {{ s__('ProjectSettings|Invited by') }}:
                {{
                  invitation.inviter.name ||
                  invitation.inviter.username ||
                  `User ${invitation.inviter.id}`
                }}
              </span>
            </div>
            <div
              v-if="invitation.errors && invitation.errors.length > 0"
              style="
                margin-top: 8px;
                padding: 6px 10px;
                background: #ffebee;
                border: 1px solid #ef5350;
                border-radius: 4px;
                font-size: 12px;
                color: #c62828;
              "
            >
              <div v-for="(error, idx) in invitation.errors" :key="idx" style="margin-top: 4px">
                {{ error.message || error }}
              </div>
            </div>
          </div>
          <div
            v-if="invitation.status === 'active' || invitation.status === 'to_deliver'"
            style="margin-left: 12px"
          >
            <button
              class="btn-sm btn-danger"
              :disabled="revokingInvitationId === invitation.id"
              @click="revokeInvitation(invitation)"
            >
              <span class="btn-text">{{ s__('ProjectSettings|Revoke') }}</span>
              <TrashIcon />
            </button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf } from '@/locale'
import { showConfirm } from '@/utils/telegramWebApp'
import UserSelect from '../common/UserSelect.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import CopyIcon from '../common/icons/CopyIcon.vue'
import TrashIcon from '../common/icons/TrashIcon.vue'

const telegramSupportHandle = '@ArtemkaGro'
const supportEmail = 'support@tracktor.space'
const copyTelegramLabel = s__('Project|Copy Telegram username')
const copyEmailLabel = s__('Project|Copy support email')

const props = defineProps({
  projectId: {
    type: String,
    required: true,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:expanded'])

const invitations = ref([])
const newInvitationUserId = ref(null)
const creatingInvitation = ref(false)
const invitationError = ref('')
const revokingInvitationId = ref(null)
const copyFeedbackKind = ref(null)
const copyFeedbackTick = ref(0)
const memberLimitNoticeEl = ref(null)
const memberLimitNoticeEmphasize = ref(false)

const INVITATIONS_QUERY = gql`
  query ProjectInvitations($id: ID!) {
    project(id: $id) {
      id
      freeMemberLimitReached
      invitations {
        id
        status
        deliveryAttempts
        errors
        history
        user {
          id
          username
          name
        }
        inviter {
          id
          username
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`

const CREATE_INVITATION = gql`
  mutation CreateInvitation($projectId: ID!, $userId: ID!) {
    createInvitation(projectId: $projectId, userId: $userId) {
      invitation {
        id
        status
        deliveryAttempts
        errors
        history
        user {
          id
          username
          name
        }
        inviter {
          id
          username
          name
        }
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const REVOKE_INVITATION = gql`
  mutation RevokeInvitation($id: ID!) {
    revokeInvitation(id: $id) {
      invitation {
        id
        status
        deliveryAttempts
        errors
        history
        user {
          id
          username
          name
        }
        inviter {
          id
          username
          name
        }
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const {
  load: loadInvitations,
  result: invitationsResult,
  loading,
  refetch: refetchInvitations,
} = useLazyQuery(INVITATIONS_QUERY, () => ({
  id: props.projectId,
}))

const { mutate: createInvitationMutation } = useMutation(CREATE_INVITATION)
const { mutate: revokeInvitationMutation } = useMutation(REVOKE_INVITATION)

const memberLimitReached = computed(() =>
  Boolean(invitationsResult.value?.project?.freeMemberLimitReached)
)

const copiedLabel = computed(() => s__('Project|Copied!'))

function onMemberLimitNoticeEmphasizeEnd() {
  memberLimitNoticeEmphasize.value = false
}

function emphasizeMemberLimitNotice() {
  if (!memberLimitReached.value) return
  memberLimitNoticeEmphasize.value = false
  nextTick(() => {
    memberLimitNoticeEmphasize.value = true
    const el = memberLimitNoticeEl.value
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
}

// Watch for query results
watch(
  invitationsResult,
  data => {
    if (data?.project?.invitations) {
      invitations.value = data.project.invitations || []
    }
  },
  { immediate: true }
)

// Watch for expanded prop to load data
watch(
  () => props.expanded,
  expanded => {
    if (expanded && !invitationsResult.value && !loading.value) {
      loadInvitations()
    }
  },
  { immediate: true }
)

// Clear error when user selects another entry
watch(newInvitationUserId, () => {
  invitationError.value = ''
})

function onCopyFeedbackAnimationEnd(event) {
  const name = String(event.animationName || '')
  if (!name.includes('copy-contact-feedback-pop')) return
  copyFeedbackKind.value = null
}

async function copyContact(kind) {
  const text = kind === 'telegram' ? telegramSupportHandle : supportEmail
  if (!text) return
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    copyFeedbackKind.value = kind
    copyFeedbackTick.value += 1
  } catch {
    // Clipboard may be denied in some contexts
  }
}

function formatInvitationStatus(status) {
  const statusMap = {
    to_deliver: s__('ProjectSettings|Pending'),
    active: s__('ProjectSettings|Pending'),
    accepted: s__('ProjectSettings|Accepted'),
    declined: s__('ProjectSettings|Declined'),
    expired: s__('ProjectSettings|Expired'),
    revoked: s__('ProjectSettings|Revoked'),
    failed_to_deliver: s__('ProjectSettings|Failed to Deliver'),
  }
  return statusMap[status] || status
}

async function createInvitation() {
  if (!newInvitationUserId.value) return

  creatingInvitation.value = true
  invitationError.value = ''

  try {
    const { data } = await createInvitationMutation({
      projectId: props.projectId,
      userId: String(newInvitationUserId.value),
    })

    if (data?.createInvitation?.errors?.length) {
      invitationError.value = data.createInvitation.errors.join(', ')
    } else {
      newInvitationUserId.value = null
      invitationError.value = ''

      // Ensure section is expanded
      if (!props.expanded) {
        emit('update:expanded', true)
      }

      // Refetch invitations to get the new one
      // Always refetch to ensure we get fresh data from the server
      if (invitationsResult.value) {
        const result = await refetchInvitations()
        // Update invitations from refetch result
        if (result?.data?.project?.invitations) {
          invitations.value = result.data.project.invitations || []
        }
      } else {
        await loadInvitations()
      }
    }
  } catch (e) {
    invitationError.value =
      s__('ProjectSettings|Failed to create invitation') +
      ': ' +
      (e.message || s__('ProjectSettings|Unknown error'))
  } finally {
    creatingInvitation.value = false
  }
}

async function revokeInvitation(invitation) {
  const confirmed = await showConfirm(
    sprintf(s__('ProjectSettings|Are you sure you want to revoke the invitation for %{user}?'), {
      user: invitation.user.name || invitation.user.username || `User ${invitation.user.id}`,
    })
  )
  if (!confirmed) return

  revokingInvitationId.value = invitation.id

  try {
    const { data } = await revokeInvitationMutation({
      id: invitation.id,
    })

    if (data?.revokeInvitation?.errors?.length) {
      alert(data.revokeInvitation.errors.join(', '))
    } else {
      // Refetch invitations to reflect the revoked status
      if (invitationsResult.value) {
        const result = await refetchInvitations()
        // Update invitations from refetch result
        if (result?.data?.project?.invitations) {
          invitations.value = result.data.project.invitations || []
        }
      } else {
        await loadInvitations()
      }
    }
  } catch (e) {
    alert(
      s__('ProjectSettings|Failed to revoke invitation') +
        ': ' +
        (e.message || s__('ProjectSettings|Unknown error'))
    )
  } finally {
    revokingInvitationId.value = null
  }
}

// Expose methods for parent component if needed
defineExpose({
  loadInvitations,
  refetchInvitations,
})
</script>

<style src="../../styles/forms.css"></style>

<style scoped>
.invitations-list {
  width: 100%;
}

.info-block {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  background: #f8f9fa;
}

.info-block-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f1f5f9;
}

.info-icon {
  font-size: 16px;
  display: inline-flex;
  color: #2aabee;
}

.info-title {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.info-block-content {
  padding: 10px 12px;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.info-block-content p {
  margin: 0;
}

.member-limit-notice {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  text-align: left;
  scroll-margin-top: 12px;
}

.member-limit-notice__text {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #666;
}

.member-limit-notice__text + .member-limit-notice__text {
  margin-top: 10px;
}

.member-limit-notice--emphasize {
  animation: member-limit-notice-emphasize 1.35s ease forwards;
}

@keyframes member-limit-notice-emphasize {
  0%,
  100% {
    border-color: #e5e7eb;
    box-shadow: none;
    background: #fafafa;
  }
  12%,
  55% {
    border-color: #2aabee;
    box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.22);
    background: #f0f9ff;
  }
}

.invite-controls-row {
  position: relative;
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.invite-controls-row__select {
  flex: 1;
  min-width: 0;
}

.invite-controls-row__submit {
  align-self: center;
  min-height: 38px;
}

.member-limit-invite-intercept {
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: pointer;
  border-radius: 4px;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.status-to_deliver {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
}

.status-badge.status-active {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #17a2b8;
}

.status-badge.status-accepted {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #28a745;
}

.status-badge.status-declined {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #dc3545;
}

.status-badge.status-expired {
  background-color: #e2e3e5;
  color: #383d41;
  border: 1px solid #6c757d;
}

.status-badge.status-revoked {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #dc3545;
}

.status-badge.status-failed_to_deliver {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #dc3545;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 14px;
}

.btn-danger {
  background: #dc3545;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-primary {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-icon {
  display: none;
}

@media (max-width: 640px) {
  .btn-text {
    display: none;
  }

  .btn-icon {
    display: inline;
  }
}
</style>
